import { FiCopy } from 'react-icons/fi';
import {
  Box,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { EvaluationEvent } from '@keyops-cep/api-client';
import { useQuery } from '@tanstack/react-query';

import {
  generateEvaluationEventQRCodePdf,
  getEvaluationEventQRCode,
} from '../../../api/evaluation-event.functions';
import {
  KeyOpsLinkButton,
  KeyOpsPrimaryButton,
} from '../../../components/ButtonComponents';
import Loading from '../../../components/Loading';
import i18n from '../../../utils/i18n';

type QRCodeModalProps = {
  isOpen: boolean;
  onClose: () => void;
  event: Pick<
    EvaluationEvent,
    'id' | 'name' | 'externalId' | 'startDate' | 'endDate'
  >;
};

const downloadPdf = (
  data: Blob,
  event: Pick<
    EvaluationEvent,
    'id' | 'name' | 'externalId' | 'startDate' | 'endDate'
  >
) => {
  const url = window.URL.createObjectURL(data);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${event.externalId}-info-sheet.pdf`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

export const QRCodeModal = ({ isOpen, onClose, event }: QRCodeModalProps) => {
  const toast = useToast();

  const {
    data: qrDataUrl,
    isLoading: isQRCodeLoading,
    isError: isQRCodeError,
  } = useQuery(
    ['get-evaluation-events-qr-code', event.id],
    () => getEvaluationEventQRCode(event.id),
    {
      enabled: !!event.id,
    }
  );

  const {
    data: pdfData,
    isLoading: isPDFDataLoading,
    isError: isPdfDataError,
    refetch: refetchPdfData,
  } = useQuery({
    queryKey: ['evaluation-event-qr-code-pdf', event.id],
    queryFn: () => generateEvaluationEventQRCodePdf(event.id),
    enabled: !!event.id, // Only run query if event.id is present
  });

  const copyToClipboard = async () => {
    if (!qrDataUrl) return;
    try {
      const blob = await (await fetch(qrDataUrl)).blob();
      await navigator.clipboard.write([
        new ClipboardItem({ [blob.type]: blob }),
      ]);
      toast({
        title: i18n.t('evaluationEvent.qrCodeModal.copiedToClipboard'),
        status: 'success',
        variant: 'subtle',
        duration: 2000,
        position: 'top-right',
        isClosable: true,
      });
    } catch {
      toast({
        title: i18n.t('evaluationEvent.qrCodeModal.failedToCopy'),
        status: 'error',
        variant: 'subtle',
        position: 'top-right',
        isClosable: true,
      });
    }
  };

  const handleDownloadPdf = async () => {
    if (!pdfData) return;
    if (isPdfDataError) {
      toast({
        title: i18n.t('errorComponent.title'),
        variant: 'subtle',
        isClosable: true,
        status: 'error',
        position: 'top-right',
      });
      return;
    }
    try {
      refetchPdfData();
      if (pdfData) {
        await downloadPdf(pdfData, event);
        toast({
          title: i18n.t('common.success'),
          variant: 'subtle',
          isClosable: true,
          status: 'success',
          position: 'top-right',
        });
      }
    } catch (e) {
      console.error(e);
      toast({
        title: i18n.t('errorComponent.title'),
        variant: 'subtle',
        isClosable: true,
        status: 'error',
        position: 'top-right',
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{i18n.t('evaluationEvent.qrCodeModal.title')}</ModalHeader>
        <ModalCloseButton />
        <ModalBody justifyItems={'center'}>
          <Text pb={2}>{event.name}</Text>
          <Box my={4} p={6} border={'1px solid #7C8DB533'} borderRadius={'md'}>
            {isQRCodeLoading && !qrDataUrl && <Loading />}
            {isQRCodeError && !qrDataUrl && (
              <Text>{i18n.t('errorComponent.title')}</Text>
            )}
            {qrDataUrl && (
              <Image src={qrDataUrl} alt="QR Code" width={200} height={200} />
            )}
          </Box>
        </ModalBody>
        <ModalFooter justifyContent={'center'} pb={10}>
          <VStack gap={3}>
            {!isPdfDataError && (
              <KeyOpsPrimaryButton
                size={'sm'}
                isLoading={isPDFDataLoading}
                isDisabled={!qrDataUrl}
                onClick={handleDownloadPdf}
                px={10}
              >
                {i18n.t('evaluationEvent.qrCodeModal.saveAsPdf')}
              </KeyOpsPrimaryButton>
            )}
            <KeyOpsLinkButton
              size={'sm'}
              leftIcon={<FiCopy />}
              isLoading={isQRCodeLoading}
              isDisabled={!qrDataUrl}
              _hover={{ textDecoration: 'none' }}
              onClick={copyToClipboard}
            >
              {i18n.t('evaluationEvent.qrCodeModal.copyImage')}
            </KeyOpsLinkButton>
          </VStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
